<template>
  <div class="math-input-container">
    <!-- Barra de herramientas matemáticas -->
    <MathEditorToolbar @insert="insertAtCursor" @wrap="wrapSelection" class="mb-3" />

    <!-- Input de texto -->
    <v-textarea
      ref="textarea"
      :model-value="modelValue"
      @update:model-value="handleInput"
      :label="label"
      :rules="rules"
      :rows="rows"
      :auto-grow="autoGrow"
      :variant="variant"
      :density="density"
      :counter="counter"
      :hint="hint || defaultHint"
      :persistent-hint="persistentHint"
      :error="error"
      :error-messages="errorMessages"
    />

    <!-- Vista previa del renderizado -->
    <v-card v-if="modelValue && modelValue.trim()" variant="outlined" class="mt-3 pa-4">
      <div class="text-caption text-grey mb-2">📊 Vista previa:</div>
      <MathRenderer :content="modelValue" />
    </v-card>
  </div>
</template>

<script>
import MathRenderer from './MathRenderer.vue'
import MathEditorToolbar from './MathEditorToolbar.vue'

export default {
  name: 'MathInput',

  components: {
    MathRenderer,
    MathEditorToolbar,
  },

  props: {
    modelValue: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Texto',
    },
    rules: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: [Number, String],
      default: 3,
    },
    autoGrow: {
      type: Boolean,
      default: true,
    },
    variant: {
      type: String,
      default: 'outlined',
    },
    density: {
      type: String,
      default: undefined,
    },
    counter: {
      type: [Number, String],
      default: undefined,
    },
    hint: {
      type: String,
      default: '',
    },
    persistentHint: {
      type: Boolean,
      default: true,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: [String, Array],
      default: '',
    },
  },

  computed: {
    defaultHint() {
      if (this.hint) return this.hint
      return '✏️ Escribe texto normal. Usa B/I para negrita/cursiva, o los botones matemáticos para fórmulas'
    },
  },

  methods: {
    handleInput(value) {
      this.$emit('update:modelValue', value)
    },

    insertAtCursor(text) {
      const textarea = this.$refs.textarea?.$el?.querySelector('textarea')
      if (!textarea) {
        // Si no podemos acceder al textarea, simplemente agregamos al final
        const newValue = (this.modelValue || '') + text
        this.$emit('update:modelValue', newValue)
        return
      }

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const currentValue = this.modelValue || ''

      // Insertar el texto en la posición del cursor
      const before = currentValue.substring(0, start)
      const after = currentValue.substring(end)
      const newValue = before + text + after

      this.$emit('update:modelValue', newValue)

      // Restaurar el foco y posicionar el cursor después del texto insertado
      this.$nextTick(() => {
        textarea.focus()
        const newCursorPos = start + text.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      })
    },

    wrapSelection({ prefix, suffix }) {
      const textarea = this.$refs.textarea?.$el?.querySelector('textarea')
      const currentValue = this.modelValue || ''

      if (!textarea) {
        const placeholder = `${prefix}texto${suffix}`
        this.$emit('update:modelValue', currentValue + placeholder)
        return
      }

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selected = currentValue.substring(start, end)

      let newValue, newStart, newEnd

      if (selected) {
        // Envolver el texto seleccionado
        const wrapped = `${prefix}${selected}${suffix}`
        newValue = currentValue.substring(0, start) + wrapped + currentValue.substring(end)
        newStart = start + prefix.length
        newEnd = end + prefix.length
      } else {
        // Sin selección: insertar placeholder y seleccionar "texto"
        const placeholder = `${prefix}texto${suffix}`
        newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end)
        newStart = start + prefix.length
        newEnd = start + prefix.length + 5 // longitud de "texto"
      }

      this.$emit('update:modelValue', newValue)

      this.$nextTick(() => {
        textarea.focus()
        textarea.setSelectionRange(newStart, newEnd)
      })
    },
  },
}
</script>

<style scoped>
.math-input-container {
  width: 100%;
}
</style>
